import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthenticatedRequest } from '../../lib/useAuthenticatedRequest';
import { config } from '../../config/env';
import { supabase } from '../../lib/supabase';
import { PlaybookModal } from '../../components/modals/PlaybookModal';

const key = (owner: string) => `topclip:submission-guide:${owner}`;
export const recordSubmissionGuideOffer = (owner: string, job: string) => {
  try { sessionStorage.setItem(key(owner), JSON.stringify({ job, created: Date.now() })); }
  catch { /* Upsell storage must never turn successful submission into failure. */ }
};

export const SubmissionGuideOffer: React.FC<{ jobId?: string }> = ({ jobId }) => {
  const { user } = useAuth();
  return user ? <OwnerOffer key={`${user.id}:${jobId ?? ''}`} owner={user.id} jobId={jobId} /> : null;
};

const OwnerOffer: React.FC<{ owner: string; jobId?: string }> = ({ owner, jobId }) => {
  const request = useAuthenticatedRequest();
  const [openFor, setOpenFor] = useState<string | null>(null);
  useEffect(() => {
    if (!jobId) return;
    let pending: { job?: string; created?: number };
    try { pending = JSON.parse(sessionStorage.getItem(key(owner)) ?? 'null'); }
    catch { return; }
    if (!pending || pending.job !== jobId || typeof pending.created !== 'number'
        || Date.now() - pending.created > 600_000 || pending.created > Date.now()) return;
    let current = true;
    const timer = window.setTimeout(() => {
      // Consume this successful-submission trigger; do not resurrect on old jobs.
      try { sessionStorage.removeItem(key(owner)); } catch { return; }
      void request(async ({ ownerId, accessToken, signal, assertCurrent }) => {
        if (!current || ownerId !== owner) return;
        // Atomic conditional update prevents repeat offers across tabs/visits.
        const { data, error } = await supabase.from('user_profiles')
          .update({ seen_pdf_popup: true }).eq('id', ownerId)
          .eq('purchased_pdf', false).eq('seen_pdf_popup', false)
          .select('id').abortSignal(signal);
        assertCurrent();
        if (!current || error || data?.length !== 1 || data[0].id !== ownerId) return;
        // Profile flags can lag a completed payment. Verify with Stripe-backed
        // access after the claim, immediately before showing the purchase offer.
        const response = await fetch(`${config.API_BASE_URL}/api/stripe/guide-access`, {
          headers: { Authorization: `Bearer ${accessToken}` }, signal,
        });
        if (!response.ok) return;
        const access = await response.json();
        assertCurrent();
        if (current && access?.owned === false) setOpenFor(jobId);
      }).catch(() => { /* If ownership/seen-state cannot be checked, skip the upsell. */ });
    }, 5000);
    return () => { current = false; window.clearTimeout(timer); };
  }, [owner, jobId, request]);
  return <PlaybookModal isOpen={!!jobId && openFor === jobId} onClose={() => setOpenFor(null)} />;
};
